import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import { ImageScaleView, ImageSliceView } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { createWaiterInView } from 'src/lib/utils';
import sounds from 'src/lib/sounds';
import { Feature } from 'src/replicant/analytics';
import { DaycareId } from 'src/replicant/daycare';
import { DaycareOptionPanel } from './DaycareOptionPanel';
import StateObserver from '../../StateObserver';
import { formatDuration } from '../../lib/formatDuration';
import { duration } from '../../replicant/utils/duration';
import * as Fate from '../../replicant/fate';

import { AssetGroup } from '@play-co/timestep-core';
import { getDaycareId } from '../../replicant/daycare/daycare.getters';

type CloseHandler = (result?: boolean) => void;
const ui = uiConfig.buruburu.care.confirmation;

export default class PopupExitDaycare extends PopupBasic {
  private close: CloseHandler;
  private timeRemaining: LangBitmapFontTextView;
  private okButton: ButtonScaleViewWithText;
  private waiter = createWaiterInView(this.box);

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => opts.close(),
      ...ui.popup,
      titleIcon: {
        image: 'assets/ui/daycare/icons/banner_daycare_icon.png',
      },
      tracking: {
        feature: Feature.DAYCARE._,
        $subFeature: Feature.DAYCARE.EXIT,
      },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();

    this.message.updateOpts({
      ...ui.message,
      width: this.box.style.width - 2 * ui.message.x,
    });

    this.box.addSubview(
      new ImageScaleView({
        ...ui.underline,
        width: this.message.style.width,
      }),
    );

    const timeRow = this.box.addSubview(new ImageSliceView(ui.costRow));

    timeRow.addSubview(
      new LangBitmapFontTextView({
        ...ui.highlightRowLabel,
        localeText: () => i18n('daycare.timeRemaining'),
      }),
    );

    this.timeRemaining = timeRow.addSubview(
      new LangBitmapFontTextView({
        ...ui.highlightRowValue,
        offsetX: ui.currency.width - 5,
      }),
    );

    this.box.addSubview(
      new LangBitmapFontTextView({
        ...ui.highlightRowLabel,
        ...ui.balanceRow,
        offsetX: ui.highlightRowLabel.x,
        localeText: () => i18n('daycare.exitNote'),
      }),
    );

    this.box.addSubview(
      new ButtonScaleViewWithText({
        ...ui.backButton,
        localeText: () => i18n('basic.back'),
        onClick: async () => {
          sounds.sfx.play('cancel');
          return opts.close(false);
        },
        tracking: {
          feature: Feature.DAYCARE._,
          $subFeature: Feature.DAYCARE.EXIT_BACK,
        },
      }),
    );

    this.okButton = new ButtonScaleViewWithText({
      ...ui.okButton,
      superview: this.box,
      localeText: () => i18n('daycare.exitOk'),
      onClick: async () => {
        sounds.sfx.play('ok');
        return opts.close(true);
      },
      tracking: {
        feature: Feature.DAYCARE._,
        $subFeature: Feature.DAYCARE.EXIT_OK,
      },
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup(['assets/ui/daycare/icons/banner_daycare_icon.png']);
  }

  public init(opts: { daycareId?: DaycareId } = {}) {
    super.init({
      title: i18n('daycare.exitTitle'),
      message: i18n('daycare.exitMessage'),
    });

    const { user } = StateObserver.getState();
    const { daycareId = getDaycareId() } = opts;
    this.box.addSubview(new DaycareOptionPanel({ daycareId }));

    const expiresAt = Fate.getters.getPermanentStatusEffectEndTime(
      user,
      'daycare',
      StateObserver.now(),
    );

    if (expiresAt) {
      this.updateCountdownTimer(expiresAt);
    }
  }

  private updateCountdownTimer(expiresAt: number) {
    let timeRemaining = expiresAt - StateObserver.now();
    if (timeRemaining < 0) {
      this.close(false);
      return;
    }

    this.timeRemaining.localeText = () => formatDuration(timeRemaining);
    this.waiter.wait(
      () => this.updateCountdownTimer(expiresAt),
      duration({ seconds: 1 }),
    );
  }
}
